import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./all-service-requests-page.module.scss";
import CustomerPageLayout from "../../customer-page-layout/CustomerPageLayout";
import { CardFrame } from "../../../../common/components/card-frame/CardFrame";
import SearchInput from "../../../../common/components/xepp/search-input/SearchInput";
import { ChevronRightIcon } from "../../../../assets/icons";
import { useLazyGetServiceRequestTypesQuery } from "../../../../service/customersupport-service";
import { ServiceRequestType } from "../../../../model/service-request/service-request";
import { ApplicationRoutes } from "../../../../routes";
import Can from "../../../../guards/Can";
import { CustomerPermissions } from "../../../../model/permissions/permissions";

const PAGE_SIZE = 10;
const LOADING_COUNT = 3;

export const AllServiceRequestsPage: React.FC = () => {
	const navigate = useNavigate();
	const [fetchServiceRequests, { isError }] = useLazyGetServiceRequestTypesQuery();

	const [items, setItems] = useState<ServiceRequestType[]>([]);
	const [hasMore, setHasMore] = useState(true);
	const [initialized, setInitialized] = useState(false);
	const [isFetchingMore, setIsFetchingMore] = useState(false);

	const pageRef = useRef(1);
	const searchRef = useRef("");
	const loadingRef = useRef(false);
	const hasMoreRef = useRef(true);
	const observerRef = useRef<IntersectionObserver | null>(null);
	const initializedRef = useRef(false);

	const loadNextPage = useCallback(async () => {
		if (loadingRef.current || !hasMoreRef.current) return;
		loadingRef.current = true;
		setIsFetchingMore(true);

		const result = await fetchServiceRequests({
			page: pageRef.current,
			pageSize: PAGE_SIZE,
			isPublished: true,
			search: searchRef.current || undefined,
		});

		if (result.data) {
			setItems(prev => [...prev, ...result.data!.items]);
			const hasNext = pageRef.current < result.data.totalPages;
			hasMoreRef.current = hasNext;
			setHasMore(hasNext);
			pageRef.current += 1;
		}

		loadingRef.current = false;
		setIsFetchingMore(false);
		if (!initializedRef.current) {
			initializedRef.current = true;
			setInitialized(true);
		}
	}, [fetchServiceRequests]);

	useEffect(() => {
		loadNextPage();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const sentinelCallback = useCallback(
		(node: HTMLDivElement | null) => {
			if (observerRef.current) observerRef.current.disconnect();
			if (!node) return;
			observerRef.current = new IntersectionObserver(
				(entries) => { if (entries[0].isIntersecting) loadNextPage(); },
				{ rootMargin: "100px", threshold: 0.1 }
			);
			observerRef.current.observe(node);
		},
		[loadNextPage]
	);

	const handleSearch = useCallback((value: string) => {
		if (observerRef.current) observerRef.current.disconnect();
		searchRef.current = value;
		pageRef.current = 1;
		hasMoreRef.current = true;
		loadingRef.current = false;
		initializedRef.current = false;
		setItems([]);
		setHasMore(true);
		setInitialized(false);
		setIsFetchingMore(false);

		(async () => {
			loadingRef.current = true;
			setIsFetchingMore(true);
			const result = await fetchServiceRequests({
				page: 1,
				pageSize: PAGE_SIZE,
				isPublished: true,
				search: value || undefined,
			});
			if (result.data) {
				setItems(result.data.items);
				const hasNext = 1 < result.data.totalPages;
				hasMoreRef.current = hasNext;
				setHasMore(hasNext);
				pageRef.current = 2;
			}
			loadingRef.current = false;
			setIsFetchingMore(false);
			initializedRef.current = true;
			setInitialized(true);
		})();
	}, [fetchServiceRequests]);

	const handleItemPress = (serviceTypeId: string) => {
		navigate(`/${ApplicationRoutes.CUSTOMER_SERVICE_REQUEST_CREATE}`, {
			state: { serviceRequestId: serviceTypeId },
		});
	};

	const renderSkeleton = () => (
		<CardFrame>
			{Array.from({ length: LOADING_COUNT }).map((_, i) => (
				<React.Fragment key={i}>
					<div className={styles.skeletonRow} />
					{i < LOADING_COUNT - 1 && <div className={styles.divider} />}
				</React.Fragment>
			))}
		</CardFrame>
	);

	return (
		<CustomerPageLayout
			title="All Service Requests"
			contentContainerClass={styles.pageContent}
		>
			<div className={styles.container}>
				<SearchInput
					placeholder="Search..."
					onSearch={handleSearch}
					debounceTime={400}
					className={styles.searchBar}
				/>

				{!initialized ? (
					renderSkeleton()
				) : isError && items.length === 0 ? (
					<div className={styles.emptyContainer}>
						<span className={styles.emptyText}>Failed to load service types</span>
					</div>
				) : items.length === 0 ? (
					<div className={styles.emptyContainer}>
						<span className={styles.emptyText}>No service types found</span>
					</div>
				) : (
					<>
						<Can permission={CustomerPermissions.AddNewServiceRequest}>
						<CardFrame>
							{items.map((item, i) => (
								<React.Fragment key={item.id}>
									<button
										className={styles.itemRow}
										onClick={() => handleItemPress(item.id)}
									>
										<span className={styles.itemName}>{item.name}</span>
										<ChevronRightIcon className={styles.chevron} aria-hidden />
									</button>
									{i < items.length - 1 && <div className={styles.divider} />}
								</React.Fragment>
							))}
						</CardFrame>
						</Can>

						{hasMore && (
							<div ref={sentinelCallback} className={styles.sentinel}>
								{isFetchingMore && <div className={styles.bottomLoader} />}
							</div>
						)}
					</>
				)}
			</div>
		</CustomerPageLayout>
	);
};
